/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import {
  ErrorMessageValidator,
  ILanguages,
  OptionTypeBase,
  SignUpFormValidator,
  ReactSelectSearch,
} from "@advapacs/common";
import useRegionDropdown from "@src/hooks/useRegionDropdown";
import { TFunction } from "i18next";
import React, {
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { TermAndServiceButton } from "@src/components/TermsAndService/TermAndServiceButton";

interface PreferencesFormFormProps {
  t: TFunction;
  wrapperClass?: string;
  languages: Array<ILanguages>;
  disabled?: boolean;
}

interface IPreferencesState {
  defaultRegion: string;
  lang: string;
  eula: boolean;
}

const defaultPreferences: Readonly<IPreferencesState> = {
  defaultRegion: "",
  lang: "en",
  eula: false,
};

export const PreferencesForm = forwardRef(
  (props: PreferencesFormFormProps, ref) => {
    const regionOptions = useRegionDropdown();
    const { t, languages, wrapperClass = "", disabled = false } = props;
    const [model, setModel] = useState<IPreferencesState>(defaultPreferences);
    const validator = useMemo(() => {
      return SignUpFormValidator(t);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t, model]);

    useImperativeHandle(
      ref,
      () => ({
        validate() {
          return {
            preferencesValidator: validator,
          };
        },
        getData() {
          return { preferencesModel: model };
        },
      }),
      [model, validator]
    );

    return (
      <div className={`preferences-form ${wrapperClass}`}>
        <div className="form-group">
          <label htmlFor="language" className="col-form-label ">
            {t("register.language")}
          </label>
          <div className="">
            <ReactSelectSearch
              options={languages.map((x) => {
                return {
                  label: x.name,
                  value: x.code,
                } as OptionTypeBase;
              })}
              isDisabled={disabled}
              name="language"
              handleSelectChange={(newValue) => {
                const option = newValue as OptionTypeBase;
                setModel({ ...model, lang: option?.value || "en" });
              }}
              value={languages
                .map((x) => {
                  return {
                    label: x.name,
                    value: x.code,
                  } as OptionTypeBase;
                })
                .find((y) => y.value === model.lang)}
              isSearchable={true}
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="defaultRegion" className="col-form-label ">
            {t("login:default_region")}
            <span className="text-danger">*</span>
          </label>
          <div className="">
            <ReactSelectSearch
              isDisabled={disabled}
              handleSelectChange={(newValue) => {
                const option = newValue as OptionTypeBase;
                setModel({ ...model, defaultRegion: option?.value });
              }}
              name="defaultRegion"
              aria-hidden="true"
              options={regionOptions}
              value={regionOptions?.find(
                (x) => x.value === model.defaultRegion
              )}
              isSearchable={true}
            />
            <ErrorMessageValidator
              errorMessage={validator.message(
                t("login:default_region"),
                model.defaultRegion,
                "required"
              )}
            />
          </div>
          <TermAndServiceButton t={t} />
        </div>
      </div>
    );
  }
);
